import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { developers } from '../utils/data';
import { CalendarIcon, MapPinIcon, GlobeIcon, TwitterIcon, LinkedinIcon, GithubIcon, CheckCircleIcon, BriefcaseIcon, CodeIcon, AwardIcon, ExternalLinkIcon, ClockIcon, MailIcon, DownloadIcon, ChevronLeftIcon, StarIcon, TrendingUpIcon } from 'lucide-react';
const DeveloperProfile = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const developer = developers.find(dev => dev.id === id);
  if (!developer) {
    return <div className="min-h-screen bg-[#0F1115] flex items-center justify-center px-4 relative" data-id="element-674">
        {/* Global Grain Texture */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 bg-grain mix-blend-overlay" data-id="element-675"></div>

        <div className="text-center relative z-10" data-id="element-676">
          <h1 className="text-2xl font-bold text-white mb-4" data-id="element-677">
            Developer Not Found
          </h1>
          <p className="text-gray-400 mb-6" data-id="element-678">
            We couldn't find the developer you're looking for.
          </p>
          <Link to="/developers" data-id="element-679">
            <button className="px-6 py-3 bg-[#d73b3e] text-white rounded-full font-semibold hover:bg-[#c02f32] transition-all duration-300" data-id="element-680">
              Back to Developers
            </button>
          </Link>
        </div>
      </div>;
  }
  // Mock data for extended profile sections
  const experience = [{
    role: developer.role,
    company: 'Tech Solutions Inc.',
    period: '2021 - Present',
    description: 'Leading backend development for high-scale SaaS applications using Laravel and AWS.'
  }, {
    role: 'Web Developer',
    company: 'Digital Agency',
    period: '2018 - 2021',
    description: 'Developed custom e-commerce solutions and CMS platforms for various clients.'
  }];
  const projects = [{
    name: 'E-commerce Platform',
    description: 'Multi-vendor marketplace built with Laravel and Vue.js',
    tags: ['Laravel', 'Vue.js', 'Stripe'],
    link: '#'
  }, {
    name: 'SaaS Dashboard',
    description: 'Analytics dashboard for social media management',
    tags: ['React', 'Tailwind', 'Chart.js'],
    link: '#'
  }, {
    name: 'API Gateway',
    description: 'Microservices architecture with Laravel and Docker',
    tags: ['Laravel', 'Docker', 'Redis'],
    link: '#'
  }];
  const certifications = [{
    name: 'Laravel Certified Developer',
    issuer: 'Laravel',
    date: '2023',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/9/9a/Laravel.svg'
  }, {
    name: 'AWS Solutions Architect',
    issuer: 'Amazon Web Services',
    date: '2022',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg'
  }];
  const testimonials = [{
    name: 'Sarah Johnson',
    role: 'CTO at TechCorp',
    content: 'Exceptional developer with deep Laravel expertise. Delivered our project ahead of schedule with outstanding quality.',
    rating: 5
  }, {
    name: 'Michael Chen',
    role: 'Product Manager',
    content: 'Great communication skills and technical knowledge. A pleasure to work with on complex projects.',
    rating: 5
  }];
  return <div className="min-h-screen bg-[#0F1115] pb-20 relative" data-id="element-681">
      {/* Global Grain Texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 bg-grain mix-blend-overlay" data-id="element-682"></div>

      {/* Cover Image */}
      <div className="h-80 w-full bg-gradient-to-br from-gray-900 via-[#1a1d24] to-gray-900 relative overflow-hidden" data-id="element-683">
        <div className="absolute inset-0 opacity-10" data-id="element-684">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#d73b3e]/20 to-transparent rounded-full blur-3xl" data-id="element-685" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-3xl" data-id="element-686" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] to-transparent opacity-80" data-id="element-687"></div>

        {/* Back Button */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 relative z-10" data-id="element-688">
          <Link to="/developers" className="inline-flex items-center text-gray-400 hover:text-white transition-colors" data-id="element-689">
            <ChevronLeftIcon className="w-5 h-5 mr-1" data-id="element-690" />
            Back to Developers
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 -mt-40 relative z-10" data-id="element-691">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-692">
          {/* Left Sidebar (Profile Info) */}
          <div className="lg:col-span-1" data-id="element-693">
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 shadow-xl overflow-hidden sticky top-24" data-id="element-694">
              <div className="p-6 text-center border-b border-gray-800" data-id="element-695">
                <div className="relative inline-block" data-id="element-696">
                  <img src={developer.avatar} alt={developer.name} className="w-32 h-32 rounded-full object-cover border-4 border-[#1a1d24] shadow-2xl mx-auto" data-id="element-697" />
                  <div className="absolute bottom-2 right-2 bg-white rounded-full p-1.5 shadow-lg" data-id="element-698">
                    <CheckCircleIcon className="w-5 h-5 text-[#d73b3e]" data-id="element-699" />
                  </div>
                </div>

                <h1 className="text-2xl font-bold text-white mt-4" data-id="element-700">
                  {developer.name}
                </h1>
                <p className="text-[#d73b3e] font-medium mt-1" data-id="element-701">
                  {developer.title}
                </p>

                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-400" data-id="element-702">
                  <MapPinIcon className="w-4 h-4" data-id="element-703" />
                  <span data-id="element-704">
                    {developer.location}, {developer.country}{' '}
                    {getFlagEmoji(developer.country)}
                  </span>
                </div>

                <div className="mt-6 flex gap-3 justify-center" data-id="element-705">
                  <button className="flex-1 px-6 py-3 bg-[#d73b3e] text-white rounded-full font-semibold hover:bg-[#c02f32] transition-all duration-300 shadow-lg" data-id="element-706">
                    Hire Me
                  </button>
                  <button className="p-3 bg-white/10 backdrop-blur-sm text-gray-300 rounded-full hover:bg-white/20 hover:text-white transition-all duration-300 border border-white/20" data-id="element-707">
                    <MailIcon className="w-5 h-5" data-id="element-708" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6" data-id="element-709">
                {/* Availability */}
                <div data-id="element-710">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-711">
                    Availability
                  </h3>
                  <div className="flex items-center justify-between mb-3" data-id="element-712">
                    <span className="text-gray-300" data-id="element-713">Status</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${developer.status === 'available' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-gray-700 text-gray-300 border border-gray-600'}`} data-id="element-714">
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${developer.status === 'available' ? 'bg-green-400' : 'bg-gray-400'}`} data-id="element-715"></span>
                      {developer.status.charAt(0).toUpperCase() + developer.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-716">
                    <span className="text-gray-300" data-id="element-717">Hourly Rate</span>
                    <span className="text-white font-bold" data-id="element-718">
                      {developer.rate.currency === 'USD' ? '$' : '€'}
                      {developer.rate.min} - {developer.rate.max}/hr
                    </span>
                  </div>
                </div>

                {/* Open To */}
                <div data-id="element-719">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-720">
                    Open To
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-721">
                    {developer.open_to.map(item => <span key={item} className="px-3 py-1.5 bg-white/5 text-gray-300 rounded-full text-xs border border-gray-800" data-id="element-722">
                        {item}
                      </span>)}
                  </div>
                </div>

                {/* Skills */}
                <div data-id="element-723">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-724">
                    Skills
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-725">
                    {developer.skills.map(skill => <span key={skill.name} className="px-3 py-1.5 bg-[#d73b3e]/10 text-[#d73b3e] rounded-full text-xs border border-[#d73b3e]/20 font-medium" data-id="element-726">
                        {skill.name}
                      </span>)}
                  </div>
                </div>

                {/* Links */}
                <div data-id="element-727">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-728">
                    Connect
                  </h3>
                  <div className="flex gap-3" data-id="element-729">
                    {developer.links.github && <a href={developer.links.github} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300 border border-gray-800" data-id="element-730">
                        <GithubIcon className="w-5 h-5" data-id="element-731" />
                      </a>}
                    {developer.links.linkedin && <a href={developer.links.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300 border border-gray-800" data-id="element-732">
                        <LinkedinIcon className="w-5 h-5" data-id="element-733" />
                      </a>}
                    {developer.links.twitter && <a href={developer.links.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300 border border-gray-800" data-id="element-734">
                        <TwitterIcon className="w-5 h-5" data-id="element-735" />
                      </a>}
                    {developer.links.website && <a href={developer.links.website} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300 border border-gray-800" data-id="element-736">
                        <GlobeIcon className="w-5 h-5" data-id="element-737" />
                      </a>}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-800" data-id="element-738">
                  <div className="flex items-center text-xs text-gray-500" data-id="element-739">
                    <CalendarIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-740" />
                    Member since{' '}
                    {new Date(developer.approvalDate).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content (Details) */}
          <div className="lg:col-span-2 space-y-8" data-id="element-741">
            {/* About Section */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-742">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-743">
                <BriefcaseIcon className="w-6 h-6 text-[#d73b3e]" data-id="element-744" />
                About Me
              </h2>
              <p className="text-gray-300 leading-relaxed text-base" data-id="element-745">
                {developer.bio}
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-746">
                <div className="bg-[#0F1115] p-5 rounded-xl border border-gray-800" data-id="element-747">
                  <div className="text-gray-400 text-sm mb-1" data-id="element-748">Experience</div>
                  <div className="text-2xl font-bold text-white" data-id="element-749">8+ Years</div>
                </div>
                <div className="bg-[#0F1115] p-5 rounded-xl border border-gray-800" data-id="element-750">
                  <div className="text-gray-400 text-sm mb-1" data-id="element-751">Projects</div>
                  <div className="text-2xl font-bold text-white" data-id="element-752">
                    40+ Completed
                  </div>
                </div>
                <div className="bg-[#0F1115] p-5 rounded-xl border border-gray-800" data-id="element-753">
                  <div className="text-gray-400 text-sm mb-1" data-id="element-754">Rating</div>
                  <div className="text-2xl font-bold text-white flex items-center gap-1" data-id="element-755">
                    5.0{' '}
                    <StarIcon className="w-5 h-5 text-yellow-500 fill-yellow-500" data-id="element-756" />
                  </div>
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-757">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-758">
                <AwardIcon className="w-6 h-6 text-[#d73b3e]" data-id="element-759" />
                Verified Certifications
              </h2>
              <div className="grid gap-4" data-id="element-760">
                {certifications.map((cert, index) => <div key={index} className="flex items-center gap-4 bg-[#0F1115] p-5 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors" data-id="element-761">
                    <div className="w-14 h-14 bg-white rounded-lg p-2.5 flex items-center justify-center flex-shrink-0" data-id="element-762">
                      <img src={cert.icon} alt={cert.issuer} className="w-full h-full object-contain" data-id="element-763" />
                    </div>
                    <div className="flex-1" data-id="element-764">
                      <h3 className="font-semibold text-white text-base" data-id="element-765">
                        {cert.name}
                      </h3>
                      <p className="text-sm text-gray-400 mt-0.5" data-id="element-766">
                        Issued by {cert.issuer} • {cert.date}
                      </p>
                    </div>
                    <div className="bg-green-500/10 text-green-400 px-3 py-1.5 rounded-full text-xs font-medium border border-green-500/20 flex items-center gap-1.5" data-id="element-767">
                      <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-768" />
                      Verified
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Experience */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-769">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-770">
                <ClockIcon className="w-6 h-6 text-[#d73b3e]" data-id="element-771" />
                Work History
              </h2>
              <div className="space-y-8" data-id="element-772">
                {experience.map((job, index) => <div key={index} className="relative pl-8 border-l-2 border-gray-800 last:border-0 pb-8 last:pb-0" data-id="element-773">
                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[#d73b3e] border-4 border-[#1a1d24]" data-id="element-774"></div>
                    <h3 className="text-lg font-semibold text-white" data-id="element-775">
                      {job.role}
                    </h3>
                    <div className="text-[#d73b3e] text-sm font-medium mb-2" data-id="element-776">
                      {job.company} • {job.period}
                    </div>
                    <p className="text-gray-400 leading-relaxed" data-id="element-777">
                      {job.description}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Projects */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-778">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-779">
                <CodeIcon className="w-6 h-6 text-[#d73b3e]" data-id="element-780" />
                Featured Projects
              </h2>
              <div className="grid sm:grid-cols-2 gap-6" data-id="element-781">
                {projects.map((project, index) => <div key={index} className="bg-[#0F1115] rounded-xl border border-gray-800 overflow-hidden group hover:border-gray-700 transition-all" data-id="element-782">
                    <div className="h-40 bg-gradient-to-br from-gray-800 to-gray-900 relative" data-id="element-783">
                      <div className="absolute inset-0 flex items-center justify-center text-gray-600" data-id="element-784">
                        <CodeIcon className="w-12 h-12" data-id="element-785" />
                      </div>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-786">
                        <a href={project.link} className="bg-white text-black px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform" data-id="element-787">
                          View Project <ExternalLinkIcon className="w-4 h-4" data-id="element-788" />
                        </a>
                      </div>
                    </div>
                    <div className="p-5" data-id="element-789">
                      <h3 className="font-semibold text-white mb-2 text-base" data-id="element-790">
                        {project.name}
                      </h3>
                      <p className="text-sm text-gray-400 mb-4 leading-relaxed" data-id="element-791">
                        {project.description}
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-792">
                        {project.tags.map(tag => <span key={tag} className="text-xs bg-white/5 text-gray-300 px-2.5 py-1 rounded-full border border-gray-800" data-id="element-793">
                            {tag}
                          </span>)}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Testimonials */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-794">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-795">
                <StarIcon className="w-6 h-6 text-[#d73b3e]" data-id="element-796" />
                Client Testimonials
              </h2>
              <div className="space-y-6" data-id="element-797">
                {testimonials.map((testimonial, index) => <div key={index} className="bg-[#0F1115] p-6 rounded-xl border border-gray-800" data-id="element-798">
                    <div className="flex gap-1 mb-3" data-id="element-799">
                      {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-800" />)}
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-4" data-id="element-801">
                      "{testimonial.content}"
                    </p>
                    <div className="flex items-center gap-3" data-id="element-802">
                      <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-white font-semibold" data-id="element-803">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div data-id="element-804">
                        <div className="font-semibold text-white text-sm" data-id="element-805">
                          {testimonial.name}
                        </div>
                        <div className="text-xs text-gray-400" data-id="element-806">
                          {testimonial.role}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="flex justify-center pt-4" data-id="element-807">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-full font-medium hover:bg-white/20 transition-all duration-300 border border-white/20" data-id="element-808">
                <DownloadIcon className="w-4 h-4" data-id="element-809" />
                Download Resume
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default DeveloperProfile;
// Helper function to get flag emoji
const getFlagEmoji = (country: string) => {
  const countryFlags: {
    [key: string]: string;
  } = {
    'United States': '🇺🇸',
    Uganda: '🇺🇬',
    Germany: '🇩🇪',
    Philippines: '🇵🇭',
    Egypt: '🇪🇬',
    Italy: '🇮🇹',
    Romania: '🇷🇴',
    France: '🇫🇷',
    Spain: '🇪🇸',
    Nigeria: '🇳🇬',
    Pakistan: '🇵🇰',
    'United Kingdom': '🇬🇧',
    Switzerland: '🇨🇭',
    Indonesia: '🇮🇩',
    Turkey: '🇹🇷'
  };
  return countryFlags[country] || '🏳️';
};